import React from "react";
import { Link } from "react-router-dom";
import { useTheme, tokens } from "./ThemeContext";
import { POSTS } from "./posts/index";

function formatDate(iso) {
  return new Date(iso).toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" });
}

const CATEGORY_COLORS = {
  "Tipped Wages": "#F59E0B",
  "FLSA":         "#818CF8",
  "Maryland":     "#10B981",
  "Onboarding":   "#60A5FA",
  "Compliance":   "#F87171",
};

function ThemeToggle({ t }) {
  const { theme, toggleTheme } = useTheme();
  return (
    <button
      onClick={toggleTheme}
      title={theme === "dark" ? "Switch to light mode" : "Switch to dark mode"}
      style={{
        display: "flex", alignItems: "center", justifyContent: "center",
        width: 32, height: 32, borderRadius: 8, cursor: "pointer",
        background: t.toggleBg, border: `1px solid ${t.navBorder}`,
        color: t.toggleColor, fontSize: 15, flexShrink: 0,
      }}
    >
      {theme === "dark" ? "☀️" : "🌙"}
    </button>
  );
}

export default function BlogIndex() {
  const { theme } = useTheme();
  const t = tokens[theme];

  React.useEffect(() => {
    document.title = "HR Compliance Blog | WorkplacePrompts.ai";
    document.body.style.background = t.appBg;
  }, [t.appBg]);

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Syne:wght@400;500;600;700;800&family=IBM+Plex+Mono:wght@400;500;600;700&display=swap');
        * { box-sizing: border-box; }
        body { background: ${t.appBg}; transition: background 0.2s; }
      `}</style>

      <div style={{ minHeight: "100vh", background: t.appBg, fontFamily: "'Syne', sans-serif", transition: "background 0.2s" }}>

        {/* Nav */}
        <div style={{ background: t.navBg, borderBottom: `1px solid ${t.navBorder}`, padding: "0 32px", height: 56, display: "flex", alignItems: "center", justifyContent: "space-between", position: "sticky", top: 0, zIndex: 100 }}>
          <Link to="/" style={{ textDecoration: "none", display: "flex", alignItems: "center", gap: 10 }}>
            <div style={{ width: 28, height: 28, background: "linear-gradient(135deg, #F59E0B, #D97706)", borderRadius: 6, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 14 }}>⚖️</div>
            <span style={{ fontSize: 14, fontWeight: 800, color: t.textPrimary, letterSpacing: "0.02em" }}>
              WorkplacePrompts<span style={{ color: t.accent }}>.ai</span>
            </span>
          </Link>
          <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
            <Link to="/" style={{ fontSize: 13, color: t.textSecondary, textDecoration: "none", fontFamily: "'IBM Plex Mono', monospace" }}>
              ← Back to tools
            </Link>
            <ThemeToggle t={t} />
          </div>
        </div>

        {/* Header */}
        <div style={{ maxWidth: 760, margin: "0 auto", padding: "56px 32px 40px" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 14 }}>
            <div style={{ width: 6, height: 6, background: t.accent, borderRadius: "50%" }} />
            <span style={{ fontSize: 11, fontWeight: 600, letterSpacing: "0.12em", textTransform: "uppercase", color: t.accent, fontFamily: "'IBM Plex Mono', monospace" }}>
              HR Compliance Resource Center
            </span>
          </div>
          <h1 style={{ fontSize: 36, fontWeight: 800, color: t.textPrimary, margin: "0 0 14px", lineHeight: 1.15 }}>
            Employer Compliance Blog
          </h1>
          <p style={{ color: t.textSecondary, fontSize: 15, margin: 0, lineHeight: 1.7, fontFamily: "'IBM Plex Mono', monospace" }}>
            Plain-English guides on wage law, tip credits, FLSA compliance, and HR requirements. Updated for 2026.
          </p>
        </div>

        {/* Post list */}
        <div style={{ maxWidth: 760, margin: "0 auto", padding: "0 32px 80px" }}>
          {POSTS.map((post) => {
            const color = CATEGORY_COLORS[post.category] || "#F87171";
            return (
              <Link key={post.slug} to={`/blog/${post.slug}`} style={{ textDecoration: "none", display: "block", marginBottom: 16 }}>
                <div
                  style={{ background: t.cardBg, border: `1px solid ${t.cardBorder}`, borderRadius: 14, padding: "24px 28px", transition: "border-color 0.18s, box-shadow 0.18s" }}
                  onMouseEnter={e => { e.currentTarget.style.borderColor = t.cardBorderHover; e.currentTarget.style.boxShadow = "0 4px 16px rgba(0,0,0,0.06)"; }}
                  onMouseLeave={e => { e.currentTarget.style.borderColor = t.cardBorder; e.currentTarget.style.boxShadow = "none"; }}
                >
                  <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 12 }}>
                    <span style={{ fontSize: 10, fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase", padding: "3px 10px", borderRadius: 20, background: `${color}14`, border: `1px solid ${color}30`, color, fontFamily: "'IBM Plex Mono', monospace" }}>
                      {post.category}
                    </span>
                    <span style={{ fontSize: 12, color: t.textSecondary, fontFamily: "'IBM Plex Mono', monospace" }}>
                      {formatDate(post.date)} · {post.readTime}
                    </span>
                  </div>
                  <h2 style={{ fontSize: 20, fontWeight: 700, color: t.textPrimary, margin: "0 0 10px", lineHeight: 1.3 }}>
                    {post.title}
                  </h2>
                  <p style={{ fontSize: 14, color: t.textSecondary, margin: "0 0 16px", lineHeight: 1.65, fontFamily: "'IBM Plex Mono', monospace" }}>
                    {post.description}
                  </p>
                  <span style={{ fontSize: 13, fontWeight: 600, color: t.accent }}>
                    Read article →
                  </span>
                </div>
              </Link>
            );
          })}
        </div>

        {/* Bottom CTA */}
        <div style={{ borderTop: `1px solid ${t.navBorder}`, padding: "40px 32px", textAlign: "center" }}>
          <p style={{ color: t.textSecondary, fontSize: 13, fontFamily: "'IBM Plex Mono', monospace", marginBottom: 16 }}>
            Need to check your current compliance status?
          </p>
          <Link to="/" style={{ display: "inline-block", padding: "12px 28px", background: t.accent, color: "#0D1321", borderRadius: 10, fontWeight: 800, fontSize: 14, textDecoration: "none", letterSpacing: "0.04em" }}>
            Use the Free Compliance Checker →
          </Link>
        </div>
      </div>
    </>
  );
}
